import { useState, useEffect } from 'preact/hooks'
import { useAuth } from '../../auth/useAuth'
import { clientCompanyApi } from '../../api/clientCompany'
import { UserManagement } from './users/UserManagement'
import type { ClientCompanyViewDto } from '@/types/company'

interface SettingsUsersProps {
  clientCompanyId?: string
}

export function SettingsUsers({ clientCompanyId }: SettingsUsersProps) {
  const { userProfile, userCompanyRoleSets } = useAuth()
  const [selectedCompany, setSelectedCompany] = useState<ClientCompanyViewDto | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const effectiveCompanyId = clientCompanyId || userProfile?.ClientCompanyId
    if (!effectiveCompanyId) {
      setError('No company selected')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    
    // Check if user has access to this company
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === effectiveCompanyId)
    
    if (!roleSet) {
      setError('You do not have access to this company')
      setSelectedCompany(null)
      setLoading(false)
      return
    }
    
    // Fetch full company data (Settings pages need complete company info)
    clientCompanyApi.getById(effectiveCompanyId)
      .then(res => {
        if (res.error || !res.data) {
          setError(res.error || 'Failed to load company')
          setSelectedCompany(null)
        } else {
          setSelectedCompany(res.data)
        }
      })
      .catch(() => {
        setError('Failed to load company')
        setSelectedCompany(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [clientCompanyId, userProfile?.ClientCompanyId, userCompanyRoleSets])

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-slate-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              Loading User Management
            </h3>
            <p className="text-slate-600 dark:text-slate-300">
              Setting up user management interface...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="text-red-700 dark:text-red-400 font-medium mb-1">Error</div>
          <div className="text-sm text-red-600 dark:text-red-300">{error}</div>
        </div>
      </div>
    )
  }

  if (!selectedCompany) return null

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          User Management
        </h1>
        <p className="text-slate-600 dark:text-slate-300">
          Manage user roles and permissions for your organization
        </p>
      </div>

      {/* User Management Content */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <UserManagement selectedCompany={selectedCompany} />
      </div>
    </div>
  )
}
